import { envVenueDefaults, type VenueConfig } from '../config/venueConfig';

export type CuisineType = {
  id: string;
  label: string;
  icon?: string;
};

export const FALLBACK_CUISINE_TYPES: CuisineType[] = [
  { id: 'burgers', label: 'Burgers', icon: '🍔' },
  { id: 'pizza', label: 'Pizza', icon: '🍕' },
  { id: 'chicken', label: 'Chicken', icon: '🍗' },
  { id: 'mexican', label: 'Mexican', icon: '🌮' },
  { id: 'asian', label: 'Asian', icon: '🍜' },
  { id: 'drinks', label: 'Drinks', icon: '🥤' },
  { id: 'snacks', label: 'Snacks', icon: '🍿' },
  { id: 'other', label: 'Other', icon: '🍽️' },
];

export type VenueSettings = VenueConfig & {
  sections?: string[] | null;
  cuisineTypes?: CuisineType[] | null;
  updatedAt?: string;
};

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8787/api';

let cachedSettings: VenueSettings | null = null;

export async function fetchVenueSettings(): Promise<VenueSettings> {
  try {
    const res = await fetch(`${API_BASE}/venue`, {
      headers: { Accept: 'application/json' },
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = await res.json();
    cachedSettings = {
      name: data.name || envVenueDefaults.name,
      color: data.brandColor || envVenueDefaults.color,
      taxRate: data.taxRate ?? envVenueDefaults.taxRate,
      serviceFee: data.serviceFee ?? envVenueDefaults.serviceFee,
      kickoffAt: data.kickoffAt || undefined,
      walkSecPerSection: data.walkSecPerSection ?? envVenueDefaults.walkSecPerSection,
      basePrepMin: data.basePrepMin ?? envVenueDefaults.basePrepMin,
      sections: data.sections ?? null,
      cuisineTypes: data.cuisineTypes ?? FALLBACK_CUISINE_TYPES,
      updatedAt: data.updatedAt,
    };
    return cachedSettings;
  } catch {
    return cachedSettings || { ...envVenueDefaults, cuisineTypes: FALLBACK_CUISINE_TYPES };
  }
}

export function getCachedVenueSettings(): VenueSettings {
  return cachedSettings || { ...envVenueDefaults, cuisineTypes: FALLBACK_CUISINE_TYPES };
}

export function applyBrandColor(color: string) {
  const root = document.documentElement;
  root.style.setProperty('--brand-primary', color);

  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(color);
  if (m) {
    const r = parseInt(m[1], 16);
    const g = parseInt(m[2], 16);
    const b = parseInt(m[3], 16);
    const dark = `#${[r, g, b].map((c) => Math.max(0, Math.min(255, c - 40)).toString(16).padStart(2, '0')).join('')}`;
    root.style.setProperty('--brand-primary-dark', dark);
    root.style.setProperty('--brand-primary-light', `rgba(${r}, ${g}, ${b}, 0.15)`);
  }
}

export function buildCuisineLookup(cuisineTypes?: CuisineType[] | null): Record<string, { label: string; icon: string }> {
  const list = cuisineTypes?.length ? cuisineTypes : FALLBACK_CUISINE_TYPES;
  return Object.fromEntries(
    list.map((c) => [c.id, { label: c.label, icon: c.icon || '🍽️' }])
  );
}
